import { Check, ChevronDown, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState, forwardRef } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DataverseFieldInfo } from "@shared/api-types";

interface FieldComboboxProps {
  fields: DataverseFieldInfo[] | undefined;
  value: string;
  onChange: (value: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  isLookupSuitableField?: (field: DataverseFieldInfo) => boolean;
  name?: string;
}

export const FieldCombobox = forwardRef<HTMLButtonElement, FieldComboboxProps>(function FieldCombobox({
  fields,
  value,
  onChange,
  isLoading = false,
  disabled = false,
  placeholder = "Select a field",
  isLookupSuitableField,
  name
}, ref) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const getFieldDisplayName = (field: DataverseFieldInfo): string => {
    return field.DisplayName?.UserLocalizedLabel?.Label ||
           field.DisplayName?.LocalizedLabels?.[0]?.Label ||
           field.SchemaName ||
           field.displayName ||
           field.logicalName;
  };

  const getFieldType = (field: DataverseFieldInfo): string | undefined => {
    return field.AttributeType || field.type;
  };

  const filteredFields = useMemo(() => {
    if (!fields) return [];

    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return fields;

    return fields.filter((field) => {
      const logicalName = (field.logicalName || field.LogicalName || "").toLowerCase();
      const schemaName = (field.SchemaName || "").toLowerCase();
      const displayName = getFieldDisplayName(field).toLowerCase();
      const fieldType = (getFieldType(field) || "").toLowerCase();

      return (
        logicalName.includes(normalizedQuery) ||
        schemaName.includes(normalizedQuery) ||
        displayName.includes(normalizedQuery) ||
        fieldType.includes(normalizedQuery)
      );
    });
  }, [fields, searchQuery]);

  const displayedFields = filteredFields.slice(0, 300);
  const selectedField = fields?.find(field => field.logicalName === value);
  const selectedDisplayName = selectedField ? getFieldDisplayName(selectedField) : value;

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          name={name}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between",
            disabled && "opacity-70 cursor-not-allowed",
            isLoading && "opacity-70"
          )}
          disabled={disabled}
        >
          {isLoading ? (
            <div className="flex items-center">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading fields...
            </div>
          ) : value && value !== "_default" && value !== "_none" ? (
            selectedDisplayName || value
          ) : (
            <span className={cn("truncate", !value && "text-muted-foreground")}>
              {placeholder}
            </span>
          )}
          <ChevronDown
            size={16}
            strokeWidth={2}
            className="ml-2 shrink-0 text-muted-foreground/80"
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full min-w-[var(--radix-popper-anchor-width)] border-input p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={`Search ${placeholder.toLowerCase()}...`}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList className="border-t border-border">
            <CommandEmpty>No field found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                key="_default"
                value="_default"
                onSelect={() => {
                  onChange("_default");
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === "_default" ? "opacity-100" : "opacity-0"
                  )}
                />
                {placeholder}
              </CommandItem>
              {placeholder === "Primary key (default)" && (
                <CommandItem
                  key="_none"
                  value="_none"
                  onSelect={() => {
                    onChange("_none");
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === "_none" ? "opacity-100" : "opacity-0"
                    )}
                  />
                  Primary key (default)
                </CommandItem>
              )}
              {displayedFields.map((field) => {
                const displayName = getFieldDisplayName(field);
                const fieldType = getFieldType(field);
                const isLookupSuitable = isLookupSuitableField && isLookupSuitableField(field);
                const fieldKey = field.logicalName || field.LogicalName || `field-${displayName}`;

                return (
                  <CommandItem
                    key={fieldKey}
                    value={fieldKey}
                    onSelect={() => {
                      const logicalName = field.logicalName ?? field.LogicalName ?? "";
                      onChange(logicalName);
                      setOpen(false);
                    }}
                    keywords={[displayName, fieldKey, fieldType || ""]}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === (field.logicalName || field.LogicalName) ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex items-center">
                      {isLookupSuitable && <span className="text-primary mr-1">★</span>}
                      <span>{displayName}</span>
                      {fieldType && (
                        <span className="text-muted-foreground ml-1 text-xs">
                          ({fieldType})
                        </span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
});
